import React from "react";
import { IUserDetail } from "../../../../types/user.api.types";
import { TextField, SelectField } from "./FormFields";
import { Modal2Button } from "../../../common/Modal2";
import { EmploymentDetail } from "./formDefaults";
import { X } from "lucide-react";
import { SelectOption } from "../../../common/Select";
import { DatePicker } from "../../../common";
import dayjs from "dayjs";
import { TextArea } from "../../../common/TextArea";
interface EmploymentHistorySectionProps {
  employments: EmploymentDetail[];
  positionOptions: SelectOption[];
  isViewMode: boolean;
  joiningDate?: string | Date;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (
    index: number,
    field: keyof EmploymentDetail,
    value: EmploymentDetail[keyof EmploymentDetail]
  ) => void;
}

const EmploymentHistorySection: React.FC<EmploymentHistorySectionProps> = ({
  employments,
  positionOptions,
  isViewMode,
  joiningDate,
  onAdd,
  onRemove,
  onChange,
}) => (
  <section className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="text-base font-bold text-[var(--color-text-primary)]">
        Previous Employment Details
      </h4>
      {!isViewMode && (
        <Modal2Button
          className="border border-[var(--color-border-primary)]"
          variant="ghost"
          size="sm"
          onClick={onAdd}
        >
          Add Employment
        </Modal2Button>
      )}
    </div>

    {employments.map((employment, index) => (
      <div
        key={`employment-${index}`}
        className="rounded-[16px] border border-[var(--color-border-secondary)] bg-surface shadow-[var(--shadow-sm)] p-4 grid grid-cols-1 md:grid-cols-2 gap-4 relative"
      >
        {!isViewMode && employments.length > 1 && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="absolute -top-2 -right-2 text-[var(--color-error)] hover:text-[var(--color-error-dark)] bg-surface border border-[var(--color-error)]/20 rounded-full p-1 shadow-sm"
            aria-label="Remove employment"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <TextField
          label="Employer Name"
          placeholder="Enter your employer name here..."
          value={employment.employerName}
          onChange={(value) => onChange(index, "employerName", value)}
          disabled={isViewMode}
        />
        <SelectField
          label="Designation"
          value={
            typeof employment?.designation === "string"
              ? employment.designation
              : employment?.designation?.id || ""
          }
          options={positionOptions}
          onChange={(index_val) => onChange(index, "designation", index_val)}
          disabled={isViewMode}
          searchable
          clearable
        />

        <DatePicker
          label="Start Date"
          value={employment.startDate ? dayjs(employment.startDate) : null}
          onChange={(date) =>
            onChange(index, "startDate", date ? date.format("YYYY-MM-DD") : "")
          }
          maxDate={joiningDate ? dayjs(joiningDate).subtract(1, "day") : dayjs()}
          placeholder="Select start date"
          disabled={isViewMode}
          format="DD/MM/YYYY"
          allowClear={false}
        />

        <DatePicker
          label="End Date"
          maxDate={joiningDate ? dayjs(joiningDate).subtract(1, "day") : dayjs()}
          value={employment.endDate ? dayjs(employment.endDate) : null}
          onChange={(date) =>
            onChange(index, "endDate", date ? date.format("YYYY-MM-DD") : "")
          }
          placeholder="Select end date"
          disabled={isViewMode}
          format="DD/MM/YYYY"
          allowClear={false}
        />

        <TextField
          label="Annual CTC"
          placeholder="E.g. 4-5 LPA"
          type="number"
          value={
            employment.annualCTC !== undefined
              ? String(employment.annualCTC)
              : ""
          }
          onChange={(value) => onChange(index, "annualCTC", Number(value) || 0)}
          min={0}
          disabled={isViewMode}
        />
        <TextArea
          label="Reason for Leaving"
          placeholder="Enter your reason for leaving here..."
          value={employment.breakReason}
          onChange={(value) => onChange(index, "breakReason", value)}
          disabled={isViewMode}
          className="md:col-span-2"
        />
      </div>
    ))}
  </section>
);

export default EmploymentHistorySection;
